/**
 * ImageGenHelper — drop-in panel that turns the teacher's natural language
 * request into a rich Nano-Banana-Flash-ready prompt, lets them edit it,
 * then renders the image. The render uses the existing studio image endpoint
 * which we just upgraded to prefer `gemini-3.1-flash-image-preview`.
 *
 * Flow:
 *   1. Teacher types a short ask ("diagram of mitosis")
 *   2. Press "Suggest prompt" → Claude expands it into a vivid Nano Banana
 *      prompt with labels list
 *   3. Teacher reviews / edits
 *   4. Press "Create image" → image returned
 *   5. Optionally "Insert into section"
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  X, Wand2, ImagePlus, Loader2, Sparkles, RefreshCw, Plus, History, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#34A853';
const SOFT = '#F0FDF4';

const STYLES = [
  { id: 'diagram',    label: 'Diagram',    sub: 'Labeled science diagram' },
  { id: 'whiteboard', label: 'Whiteboard', sub: 'Hand-drawn classroom feel' },
  { id: 'photo',      label: 'Photo',      sub: 'Realistic photograph' },
  { id: 'isometric',  label: 'Isometric',  sub: '3D iso illustration' },
  { id: '3d',         label: '3D Render',  sub: 'Premium 3D model' },
  { id: 'lab',        label: 'Lab',        sub: 'Lab equipment / apparatus' },
  { id: 'concept',    label: 'Concept',    sub: 'Minimalist concept art' },
];

function authHeaders() {
  const t = localStorage.getItem('desky_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function ImageGenHelper({ open, onClose, draftId, sectionId, sections = [], subject = null, sectionContext = '', onImageReady }) {
  const [request, setRequest] = useState('');
  const [style, setStyle] = useState('diagram');
  // Which section the generated image gets added to. Defaults to the section the
  // panel was opened from, but the "Add To" dropdown lets the teacher retarget it.
  const [targetSectionId, setTargetSectionId] = useState(sectionId || '');
  const [prompt, setPrompt] = useState('');
  const [labels, setLabels] = useState([]);
  const [renderModel, setRenderModel] = useState('gemini-3.1-flash-image-preview');
  const [suggesting, setSuggesting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [image, setImage] = useState(null);
  const [usedModel, setUsedModel] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!open) {
      setImage(null);
      setUsedModel(null);
    }
  }, [open]);

  // Keep the "Add To" target in sync with the section the panel was opened from.
  // Fall back to the first available section so Create always has a valid target.
  useEffect(() => {
    if (!open) return;
    setTargetSectionId(sectionId || sections[0]?.id || '');
  }, [open, sectionId, sections]);

  const suggest = async () => {
    if (!request.trim()) { toast.error('Type what you want'); return; }
    setSuggesting(true);
    try {
      const res = await axios.post(`${API}/teacher/studio/image/prompt-suggest`,
        { request, style, subject, section_context: sectionContext },
        { headers: authHeaders() });
      setPrompt(res.data.prompt || '');
      setLabels(res.data.labels || []);
      setRenderModel(res.data.render_model || 'gemini-3.1-flash-image-preview');
      toast.success('Prompt drafted — review and create');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Prompt suggest failed');
    } finally { setSuggesting(false); }
  };

  const create = async () => {
    // The teacher's own typed request is a valid prompt on its own — Create
    // shouldn't require the Suggest-prompt round-trip first. Prefer the
    // reviewed/edited prompt once one exists, otherwise send the request
    // text straight through.
    const effectivePrompt = prompt.trim() || request.trim();
    if (!effectivePrompt) { toast.error('Type what you want first'); return; }
    if (!draftId || !targetSectionId) {
      toast.error('Pick a section to add the image to');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/teacher/studio/generate-image`,
        { draft_id: draftId, section_id: targetSectionId, prompt: effectivePrompt },
        { headers: authHeaders() });
      setImage(res.data.image);
      setUsedModel(res.data.model || renderModel);
      setHistory(prev => [{ image: res.data.image, prompt, model: res.data.model, ts: Date.now() }, ...prev].slice(0, 5));
      toast.success(`Image rendered with ${res.data.model || renderModel}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Image generation failed');
    } finally { setCreating(false); }
  };

  const insert = () => {
    if (!image) return;
    onImageReady?.({ image, prompt, model: usedModel, sectionId: targetSectionId });
    toast.success('Image inserted into section');
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[65] flex" style={{ background: 'rgba(8,16,12,0.72)' }} data-testid="image-gen-helper">
      <div className="ml-auto h-full w-full sm:w-[640px] lg:w-[760px] bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1.5">
              <ImagePlus className="w-3 h-3"/> Image Studio · Gemini Nano Banana Flash
            </div>
            <h2 className="text-lg font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Auto-prompt &amp; create
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Say what you want — Claude drafts a rich prompt, Nano Banana Flash renders it.
            </p>
          </div>
          <button onClick={onClose} data-testid="img-close" className="text-slate-400 hover:text-slate-700 p-1 -m-1 rounded-md hover:bg-slate-100">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Request */}
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">What image do you want?</label>
            <textarea value={request} onChange={e => setRequest(e.target.value)} rows={2}
              data-testid="img-request"
              placeholder="e.g. diagram of plant mitosis · labeled chemistry beaker setup · forces on inclined plane · DNA structure"
              className="mt-2 w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-emerald-200 focus:outline-none"/>

            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Style</span>
              {STYLES.map(s => (
                <button key={s.id} onClick={() => setStyle(s.id)}
                  data-testid={`img-style-${s.id}`}
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                    style === s.id ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`} title={s.sub}>{s.label}</button>
              ))}
            </div>

            {/* Add To — pick which section the generated image is added to */}
            {sections.length > 0 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Add To</span>
                <select value={targetSectionId} onChange={e => setTargetSectionId(e.target.value)}
                  data-testid="img-add-to-section"
                  className="text-xs rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 focus:outline-none transition-all max-w-[320px]">
                  {sections.map((s, i) => (
                    <option key={s.id} value={s.id}>{`${i + 1}. ${s.title || 'Untitled section'}`}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={suggest} disabled={suggesting || !request.trim()} data-testid="img-suggest-btn"
                className="h-10 px-4 text-sm font-bold text-white rounded-xl inline-flex items-center gap-2 shadow-[0_4px_12px_rgba(52,168,83,0.25)] disabled:opacity-50 disabled:shadow-none transition-all hover:shadow-[0_6px_16px_rgba(52,168,83,0.35)] active:scale-[0.98]"
                style={{ background: `linear-gradient(135deg, ${GREEN}, #0d9488)` }}>
                {suggesting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>}
                Suggest prompt
              </button>
              {/* Always-visible Create button — renders directly from the typed
                  request; Suggest prompt is optional polish, not a gate. */}
              <button onClick={create} disabled={creating || !(prompt.trim() || request.trim())} data-testid="img-create-btn"
                className="h-10 px-4 text-sm font-bold text-white rounded-xl inline-flex items-center gap-2 shadow-[0_4px_12px_rgba(124,58,237,0.25)] disabled:opacity-40 disabled:shadow-none transition-all hover:shadow-[0_6px_16px_rgba(124,58,237,0.35)] active:scale-[0.98]"
                style={{ background: (prompt.trim() || request.trim()) ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : '#94a3b8' }}
                title={!(prompt.trim() || request.trim()) ? 'Type what you want above first' : 'Render with Nano Banana Flash'}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin"/> : <ImagePlus className="w-4 h-4"/>}
                Create image
              </button>
            </div>
          </div>

          {/* Generated prompt */}
          {prompt && (
            <div className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-[0_4px_16px_rgba(16,185,129,0.08)]" data-testid="img-prompt-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Suggested prompt · review &amp; edit</span>
                <span className="text-[10px] text-slate-500 inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/> render: {renderModel}</span>
              </div>
              <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={4}
                data-testid="img-prompt-text"
                className="w-full text-[12px] rounded-xl border border-emerald-200 bg-emerald-50/30 px-3 py-2.5 focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 focus:outline-none transition-all"/>
              {labels.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {labels.map((l, i) => (
                    <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">{l}</span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">
                <button onClick={create} disabled={creating} data-testid="img-create-btn-secondary"
                  className="h-10 px-5 text-sm font-bold text-white rounded-xl inline-flex items-center gap-2 shadow-[0_4px_12px_rgba(124,58,237,0.3)] hover:shadow-[0_6px_16px_rgba(124,58,237,0.4)] active:scale-[0.98] disabled:opacity-50 transition-all"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  {creating ? <Loader2 className="w-4 h-4 animate-spin"/> : <ImagePlus className="w-4 h-4"/>}
                  Create image with Nano Banana Flash
                </button>
                <button onClick={suggest} disabled={suggesting} data-testid="img-resuggest"
                  className="h-10 px-3 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-white hover:bg-emerald-50 rounded-xl inline-flex items-center gap-1.5 transition-all active:scale-[0.98]">
                  <RefreshCw className="w-3.5 h-3.5"/> Re-suggest
                </button>
              </div>
            </div>
          )}

          {/* Generated image */}
          {image && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="img-result-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Result</span>
                <span className="text-[10px] text-emerald-700 font-bold inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/> {usedModel}</span>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 flex items-center justify-center" style={{ minHeight: 220 }}>
                <img alt="" src={image} className="max-w-full max-h-[360px] object-contain rounded"/>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={create} disabled={creating} data-testid="img-regen-btn"
                  className="h-9 px-3 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5"/> Regenerate
                </button>
                {onImageReady && (
                  <button onClick={insert} data-testid="img-insert-btn"
                    className="ml-auto h-9 px-4 text-xs font-bold text-white rounded-lg inline-flex items-center gap-1.5 shadow"
                    style={{ background: GREEN }}>
                    <Plus className="w-3.5 h-3.5"/> Insert into section
                  </button>
                )}
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 1 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 inline-flex items-center gap-1"><History className="w-3 h-3"/> Recent renders</div>
              <div className="grid grid-cols-5 gap-2">
                {history.slice(1).map((h, i) => (
                  <button key={i} onClick={() => { setImage(h.image); setUsedModel(h.model); }}
                    className="aspect-square rounded-md overflow-hidden border border-slate-200 hover:border-emerald-300 transition-colors">
                    <img alt="" src={h.image} className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Fallback hint */}
          {!image && !prompt && (
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3 inline-flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0"/>
              <span>Type your idea above and press <strong>Create image</strong> to render it directly, or <strong>Suggest prompt</strong> to have Claude draft a richer Nano-Banana-ready prompt first for you to review and edit.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
